import { useEffect, useMemo, useRef, useState } from 'react';
import { Button, Checkbox, Input, Tooltip, message as toast } from 'antd';
import { CloseOutlined, PictureOutlined, SendOutlined, SnippetsOutlined } from '@ant-design/icons';
import type { TextAreaRef } from 'antd/es/input/TextArea';
import { SupportTemplate } from 'pages/support-shared/types';
import { renderTemplate } from 'pages/support-shared/utils';
import styles from './styles.module.scss';

const MAX_FILES = 5;
const MAX_MB = 10;

export interface SendPayload {
  content: string;
  files: File[];
  templateId?: number;
  resolve: boolean;
}

interface Props {
  templates: SupportTemplate[];
  variables: Record<string, string>;
  disabled?: boolean;
  /** Người khác đang giữ luồng này. */
  holderName?: string | null;
  onClaim?: () => void;
  /** Dòng nhắc dưới ô soạn (người dùng thấy tên ai). */
  footerNote?: string;
  onSend: (payload: SendPayload) => Promise<boolean>;
}

/** Bỏ dấu tiếng Việt để tìm văn mẫu: "cham cong" khớp "Chấm công". */
const fold = (value: string) =>
  value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/đ/g, 'd')
    .replace(/Đ/g, 'D')
    .toLowerCase();

const Composer = ({ templates, variables, disabled, holderName, onClaim, footerNote, onSend }: Props) => {
  const [text, setText] = useState('');
  const [files, setFiles] = useState<{ file: File; url: string }[]>([]);
  const [templateId, setTemplateId] = useState<number | undefined>();
  const [resolve, setResolve] = useState(false);
  const [sending, setSending] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerQuery, setPickerQuery] = useState('');
  const [cursor, setCursor] = useState(0);
  const inputRef = useRef<TextAreaRef>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  // Gõ "/" ở đầu ô → mở danh sách văn mẫu, lọc theo chữ sau dấu "/".
  const slashQuery = /^\/(\S*)$/.exec(text)?.[1];
  const showMenu = pickerOpen || slashQuery !== undefined;
  const query = pickerOpen ? pickerQuery : slashQuery || '';

  const matches = useMemo(() => {
    const q = fold(query.trim());
    const list = templates.filter((t) => t.isActive);
    if (!q) return list.slice(0, 30);
    return list
      .filter((t) => fold(`${t.shortcut || ''} ${t.title} ${t.content}`).includes(q))
      .sort((a, b) => Number(fold(b.shortcut || '').startsWith(q)) - Number(fold(a.shortcut || '').startsWith(q)))
      .slice(0, 30);
  }, [templates, query]);

  useEffect(() => setCursor(0), [query, showMenu]);
  useEffect(
    () => () => {
      files.forEach((f) => URL.revokeObjectURL(f.url));
    },
    // Chỉ dọn khi rời trang; khi xoá từng ảnh đã revoke riêng.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const pick = (tpl: SupportTemplate) => {
    setText(renderTemplate(tpl.content, variables));
    setTemplateId(tpl.id);
    setPickerOpen(false);
    setPickerQuery('');
    window.setTimeout(() => inputRef.current?.focus(), 0);
  };

  const addFiles = (list: FileList | File[]) => {
    const incoming = Array.from(list).filter((f) => f.type.startsWith('image/'));
    if (!incoming.length) return;
    const tooBig = incoming.find((f) => f.size > MAX_MB * 1024 * 1024);
    if (tooBig) {
      toast.warning(`Ảnh "${tooBig.name}" lớn hơn ${MAX_MB} MB.`);
      return;
    }
    setFiles((prev) => {
      const room = MAX_FILES - prev.length;
      if (incoming.length > room) toast.warning(`Mỗi tin tối đa ${MAX_FILES} ảnh.`);
      return [...prev, ...incoming.slice(0, Math.max(room, 0)).map((file) => ({ file, url: URL.createObjectURL(file) }))];
    });
  };

  const removeFile = (index: number) =>
    setFiles((prev) => {
      URL.revokeObjectURL(prev[index].url);
      return prev.filter((_, i) => i !== index);
    });

  const canSend = !disabled && !sending && (!!text.trim() || files.length > 0) && slashQuery === undefined;

  const send = async () => {
    if (!canSend) return;
    setSending(true);
    const ok = await onSend({ content: text.trim(), files: files.map((f) => f.file), templateId, resolve });
    setSending(false);
    if (ok) {
      files.forEach((f) => URL.revokeObjectURL(f.url));
      setFiles([]);
      setText('');
      setTemplateId(undefined);
      setResolve(false);
      window.setTimeout(() => inputRef.current?.focus(), 0);
    }
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    // Bộ gõ tiếng Việt đang ghép chữ: không coi Enter là gửi.
    if ((e.nativeEvent as any).isComposing) return;
    if (showMenu && matches.length) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setCursor((c) => (c + 1) % matches.length);
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setCursor((c) => (c - 1 + matches.length) % matches.length);
        return;
      }
      if (e.key === 'Enter' || e.key === 'Tab') {
        e.preventDefault();
        pick(matches[cursor] || matches[0]);
        return;
      }
    }
    if (e.key === 'Escape' && showMenu) {
      e.preventDefault();
      setPickerOpen(false);
      if (slashQuery !== undefined) setText('');
      return;
    }
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  return (
    <div className={styles.composer}>
      {holderName && (
        <div className={styles.holderWarn}>
          <span>
            <strong>{holderName}</strong> đang xử lý cuộc này — bạn vẫn trả lời được, nhưng nên báo trước để tránh trả lời trùng.
          </span>
          {onClaim && (
            <Button size="small" onClick={onClaim}>
              Nhận về mình
            </Button>
          )}
        </div>
      )}

      {showMenu && (
        <div className={styles.templateMenu} role="listbox" aria-label="Văn mẫu">
          {pickerOpen && (
            <div style={{ padding: 8, borderBottom: '1px solid #ECE4E9' }}>
              <Input
                autoFocus
                size="small"
                placeholder="Tìm văn mẫu…"
                value={pickerQuery}
                onChange={(e) => setPickerQuery(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Escape') setPickerOpen(false);
                  if (e.key === 'Enter' && matches[0]) pick(matches[0]);
                }}
              />
            </div>
          )}
          {matches.length ? (
            matches.map((tpl, index) => (
              <button
                type="button"
                key={tpl.id}
                role="option"
                aria-selected={index === cursor}
                className={`${styles.templateItem} ${index === cursor ? styles.templateItemActive : ''}`}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => pick(tpl)}
              >
                <strong>
                  {tpl.shortcut ? `/${tpl.shortcut} · ` : ''}
                  {tpl.title}
                </strong>
                <small>{renderTemplate(tpl.content, variables)}</small>
              </button>
            ))
          ) : (
            <div style={{ padding: 12, color: '#9A8F9E', fontSize: 13 }}>
              {templates.length ? 'Không có văn mẫu khớp.' : 'Chưa có văn mẫu — thêm ở trang Văn mẫu & cài đặt.'}
            </div>
          )}
        </div>
      )}

      {!!files.length && (
        <div className={styles.attachRow}>
          {files.map((f, index) => (
            <div className={styles.attachThumb} key={f.url}>
              <img src={f.url} alt={f.file.name} />
              <Button size="small" shape="circle" icon={<CloseOutlined />} onClick={() => removeFile(index)} aria-label="Bỏ ảnh" />
            </div>
          ))}
        </div>
      )}

      <div className={styles.composerRow}>
        <Tooltip title="Văn mẫu (hoặc gõ / ở đầu ô)">
          <Button
            icon={<SnippetsOutlined />}
            onClick={() => {
              setPickerOpen((v) => !v);
              setPickerQuery('');
            }}
            disabled={disabled}
            aria-label="Văn mẫu"
          />
        </Tooltip>
        <Tooltip title={`Gửi ảnh (tối đa ${MAX_FILES}, có thể dán ảnh chụp màn hình)`}>
          <Button
            icon={<PictureOutlined />}
            onClick={() => fileRef.current?.click()}
            disabled={disabled || files.length >= MAX_FILES}
            aria-label="Gửi ảnh"
          />
        </Tooltip>
        <input
          ref={fileRef}
          type="file"
          accept="image/png,image/jpeg,image/jpg,image/webp,image/gif"
          multiple
          hidden
          onChange={(e) => {
            if (e.target.files) addFiles(e.target.files);
            e.target.value = '';
          }}
        />
        <Input.TextArea
          ref={inputRef}
          value={text}
          disabled={disabled}
          onChange={(e) => {
            setText(e.target.value);
            if (!e.target.value) setTemplateId(undefined);
          }}
          onKeyDown={onKeyDown}
          onPaste={(e) => {
            if (e.clipboardData?.files?.length) {
              e.preventDefault();
              addFiles(e.clipboardData.files);
            }
          }}
          autoSize={{ minRows: 1, maxRows: 8 }}
          placeholder={disabled ? 'Chưa thể gửi tin.' : 'Trả lời… (Enter gửi · Shift+Enter xuống dòng · / văn mẫu)'}
          maxLength={4000}
        />
        <Button type="primary" icon={<SendOutlined />} onClick={send} loading={sending} disabled={!canSend}>
          Gửi
        </Button>
      </div>
      <div className={styles.composerActions}>
        <Checkbox checked={resolve} onChange={(e) => setResolve(e.target.checked)} disabled={disabled}>
          Gửi xong đánh dấu <strong>Đã xong</strong>
        </Checkbox>
        {footerNote && <span>{footerNote}</span>}
      </div>
    </div>
  );
};

export default Composer;
